import React, { ReactNode } from "react";
import { Form, Formik, FormikProps } from "formik";
import Box from "@material-ui/core/Box";
import MenuItem from "@material-ui/core/MenuItem";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Radio from "@material-ui/core/Radio";
import CustomButton from "../../../components/Button/CustomButton";
import CustomDropdown from "../../../components/Input/CustomDropdown";
import CustomDialog, { CustomDialogProps } from "../../../components/Dialog/CustomDialog";
import CustomTextField from "../../../components/Input/CustomTextField";
import { RequestConfig } from "../RequestConfig";
import yupValidationSchema from "./TestRequestCreationValidationSchema";

const applicationTypeMap = new Map<string, string>([
  ["Application Engineering (AE/ASE)", "604620000"],
  ["Process Engineering (AProE)", "604620001"],
]);

const applicationTypeArray = Array.from(applicationTypeMap.keys());

const initialValues = { opportunityId: "", requestType: "", withCrmOpportunityId: "No" };

interface InnerFormikProps {
  opportunityId: string;
  requestType: string;
  withCrmOpportunityId: string;
}

interface TestRequestCreationDialogProps extends CustomDialogProps {
  header: string;
  open: boolean;
  onClose: (...props: unknown[]) => void;
  handleTestRequestCreation: (params: RequestConfig) => Promise<void>;
}

const TestRequestCreationDialog = (props: TestRequestCreationDialogProps) => {
  const { header, onClose, open, handleTestRequestCreation } = props;

  const handleSubmit = (values: InnerFormikProps) => {
    const regex = /^\d{6}$/;

    if (values.opportunityId && regex.test(values.opportunityId)) {
      handleTestRequestCreation({
        aproe: values.requestType === "Process Engineering (AProE)",
        historical: false,
        opportunityId: `CRM-OPP-${values.opportunityId}` || "",
        requestType: applicationTypeMap.get(values.requestType) || "",
      });
    } else {
      handleTestRequestCreation({
        aproe: values.requestType === "Process Engineering (AProE)",
        historical: false,
        opportunityId: values.opportunityId || "",
        requestType: applicationTypeMap.get(values.requestType) || "",
      });
    }
    onClose();
  };

  return (
    <CustomDialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <h2>{header}</h2>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={yupValidationSchema}>
        {(formikProps: FormikProps<InnerFormikProps>): ReactNode => {
          const { errors, handleChange, isSubmitting, touched, values } = formikProps;

          return (
            <Form>
              <CustomDropdown
                mandatory
                autoFocus
                error={!!(errors.requestType && touched.requestType)}
                helperText={errors.requestType && touched.requestType ? errors.requestType : ""}
                name="requestType"
                labelText="Please select the type of request"
                onChange={handleChange}
                value={values.requestType}
              >
                {applicationTypeArray.map((name: string) => (
                  <MenuItem key={name} value={name}>
                    {name.toUpperCase()}
                  </MenuItem>
                ))}
              </CustomDropdown>
              <Box display="flex" flexDirection="column" marginTop="44px">
                <Box>Is this request related to a CRM Opportunity?</Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box marginRight="16px">Linked requests will be handled with higher priority.</Box>
                  <RadioGroup row defaultValue="No" name="withCrmOpportunityId" onChange={handleChange}>
                    {["Yes", "No"].map((option) => (
                      <FormControlLabel key={option} control={<Radio />} label={option} value={option} />
                    ))}
                  </RadioGroup>
                </Box>
                <Box>
                  {values.withCrmOpportunityId === "Yes" && (
                    <p>
                      Please enter the CRM Opportunity ID or initiate the request via CRM (
                      <a href="https://tesa-prd.crm4.dynamics.com/" target="_blank" rel="noopener noreferrer">
                        https://tesa-prd.crm4.dynamics.com/
                      </a>
                      ) to link the LEAP request with an Opportunity.
                    </p>
                  )}
                  <CustomTextField
                    fullWidth
                    disabled={values.withCrmOpportunityId === "No"}
                    error={!!(errors.opportunityId && touched.opportunityId)}
                    helperText={errors.opportunityId && touched.opportunityId ? errors.opportunityId : ""}
                    label="Opportunity Id"
                    name="opportunityId"
                    onChange={handleChange}
                    value={values.opportunityId}
                  />
                </Box>
              </Box>
              <Box display="flex" justifyContent="flex-end" marginTop="44px">
                <CustomButton type="submit" primary disabled={isSubmitting} text="Continue" />
              </Box>
            </Form>
          );
        }}
      </Formik>
    </CustomDialog>
  );
};

export default TestRequestCreationDialog;
